/**
 * The planner's preferences panel: global rules, seven day overrides, dates.
 *
 * This replaced a free-text box whose sentences a language model interpreted.
 * Three of those sentences had since been re-encoded as real constraints while
 * still sitting in the prose, so the same rule was stated twice and only one
 * copy was enforced; others were enforced nowhere at all. Every control here
 * writes a field the engine actually reads.
 *
 * The one idea to hold on to: a day control shows "Inherited" until it is
 * overridden, and clearing it goes back to inherited rather than to empty.
 * Absent and empty are different in the stored blob (see `plannerPreferences`),
 * and the UI has to be able to say both.
 */

import { useState } from "react";

import {
  type DoubleFeatureMode,
  type PlannerOptions,
  type PlannerPreferences,
  WEEKDAYS,
  describeDay,
  fromTiers,
  move,
  setDay,
  setGlobal,
  toClock,
  toMinutes,
  toTiers,
  toggle,
} from "../lib/plannerPreferences";

const CARD = "rounded-md border border-neutral-800 bg-neutral-950/40 p-3 space-y-3";
const FIELD = "rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-sm";
const CHIP = "rounded border px-2 py-1 text-xs";

// "Only in pairs" never said what happens to a film that has no partner, which
// is the entire behaviour of the mode.
const MODE_LABELS: Record<DoubleFeatureMode, string> = {
  off: "Never pair",
  preferred: "Pair when it fits",
  required: "Only films I can pair",
};
const MODE_HELP: Record<DoubleFeatureMode, string> = {
  off: "One film at most, even if two would fit back to back.",
  preferred: "Take a back-to-back pair when one is available, otherwise a single film.",
  required: "Suggest a film only if it pairs back to back with another; unpairable films are skipped.",
};

type Props = {
  preferences: PlannerPreferences;
  options: PlannerOptions | null;
  onChange: (next: PlannerPreferences) => void;
};

/** An ordered list with up/down controls and an optional per-row extra. */
function Ordered({
  entries,
  onMove,
  render,
  label,
}: {
  entries: Array<{ key: string }>;
  onMove: (from: number, to: number) => void;
  render: (index: number) => React.ReactNode;
  label: string;
}) {
  return (
    <ol className="space-y-1" aria-label={label}>
      {entries.map((entry, index) => (
        <li key={entry.key} className="flex items-center gap-2 text-sm">
          <span className="w-4 text-right text-xs text-neutral-600">{index + 1}</span>
          <button
            type="button"
            aria-label={`Move ${entry.key} up`}
            disabled={index === 0}
            onClick={() => onMove(index, index - 1)}
            className="rounded border border-neutral-800 px-1.5 text-xs text-neutral-400 disabled:opacity-30"
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`Move ${entry.key} down`}
            disabled={index === entries.length - 1}
            onClick={() => onMove(index, index + 1)}
            className="rounded border border-neutral-800 px-1.5 text-xs text-neutral-400 disabled:opacity-30"
          >
            ↓
          </button>
          {render(index)}
        </li>
      ))}
    </ol>
  );
}

/** A time-window editor that can also say "inherited" and "no restriction". */
function Windows({
  value,
  inherited,
  onChange,
  idPrefix,
}: {
  value: Array<[number, number]> | undefined;
  inherited?: string;
  onChange: (next: Array<[number, number]> | undefined) => void;
  idPrefix: string;
}) {
  const windows = value ?? [];
  return (
    <div className="space-y-2">
      {value === undefined && inherited && (
        <p className="text-xs text-neutral-600">Inherited: {inherited}</p>
      )}
      {windows.map(([from, to], index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            type="time"
            aria-label={`${idPrefix} window ${index + 1} from`}
            value={toClock(from)}
            onChange={(e) => {
              const minutes = toMinutes(e.target.value);
              if (minutes === null) return;
              onChange(windows.map((w, i) => (i === index ? [minutes, w[1]] : w)));
            }}
            className={FIELD}
          />
          <span className="text-xs text-neutral-600">to</span>
          <input
            type="time"
            aria-label={`${idPrefix} window ${index + 1} to`}
            value={toClock(to)}
            onChange={(e) => {
              const minutes = toMinutes(e.target.value);
              if (minutes === null) return;
              onChange(windows.map((w, i) => (i === index ? [w[0], minutes] : w)));
            }}
            className={FIELD}
          />
          <button
            type="button"
            aria-label={`Remove ${idPrefix} window ${index + 1}`}
            onClick={() => onChange(windows.filter((_w, i) => i !== index))}
            className={`${CHIP} border-neutral-800 text-neutral-500 hover:border-red-800`}
          >
            ×
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange([...windows, [14 * 60, 18 * 60]])}
          className={`${CHIP} border-neutral-700 text-neutral-400 hover:border-neutral-500`}
        >
          Add hours
        </button>
        {windows.length === 0 && value !== undefined && (
          <span className="self-center text-xs text-neutral-600">Any time</span>
        )}
        {value !== undefined && inherited && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className={`${CHIP} border-neutral-800 text-neutral-500 hover:border-neutral-600`}
          >
            Use inherited
          </button>
        )}
        {value === undefined && (
          <button
            type="button"
            onClick={() => onChange([])}
            className={`${CHIP} border-neutral-800 text-neutral-500 hover:border-neutral-600`}
          >
            Override
          </button>
        )}
      </div>
    </div>
  );
}

function DoubleFeatures({
  value,
  inherited,
  onChange,
  label,
}: {
  value: DoubleFeatureMode | undefined;
  inherited?: DoubleFeatureMode;
  onChange: (next: DoubleFeatureMode | undefined) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-neutral-400">
      <span className="w-32 shrink-0">Double features</span>
      <select
        aria-label={label}
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? undefined : (e.target.value as DoubleFeatureMode))
        }
        className={FIELD}
      >
        {inherited !== undefined && <option value="">Inherited ({MODE_LABELS[inherited]})</option>}
        <option value="off">{MODE_LABELS.off}</option>
        <option value="preferred">{MODE_LABELS.preferred}</option>
        <option value="required">{MODE_LABELS.required}</option>
      </select>
      <span className="text-xs text-neutral-600">{MODE_HELP[value ?? inherited ?? "preferred"]}</span>
    </label>
  );
}

/** A gap editor, in minutes, for the dead time between the halves of a pair. */
// Offered ceilings, matching the day profile's `double_feature_gap`. The FLOOR
// (`services/screening_gap.MIN_GAP_MINUTES`, 25) is deliberately absent: it is
// what AMC will actually sell, not a taste, so it is not the user's to set.
const GAP_CHOICES = [30, 60, 90] as const;

// Mirrors `screening_gap.DEFAULT_MAX_GAP_MINUTES`. It moved 30 -> 60 when the
// floor landed: a 30 ceiling over a 25 floor is a five-minute window that finds
// no pairs at all, so the two have to move together.
const DEFAULT_GAP = 60;

function GapField({
  value,
  inherited,
  onChange,
  label,
}: {
  value: number | undefined;
  inherited?: number;
  onChange: (next: number | undefined) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-neutral-400">
      <span className="w-32 shrink-0">Most time between</span>
      <select
        aria-label={label}
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? undefined : Number(e.target.value))
        }
        className={`${FIELD} w-32`}
      >
        {inherited != null && (
          <option value="">Inherited ({inherited} min)</option>
        )}
        {GAP_CHOICES.map((choice) => (
          <option key={choice} value={choice}>
            {choice} minutes
          </option>
        ))}
      </select>
      {/* The FLOOR is not offered because it is not a preference: below
          `screening_gap.MIN_GAP_MINUTES` the second film is unbookable, since
          AMC will not sell a seat for a screening that starts before your
          current one lets out. Only the ceiling is yours to set. */}
      <span className="text-xs text-neutral-600">
        of waiting between two films, on top of the 25-minute trailer block
      </span>
    </label>
  );
}

export default function PlannerRules({ preferences, options, onChange }: Props) {
  const [openDay, setOpenDay] = useState<number | null>(null);
  const global = preferences.global;
  const theaterEntries = fromTiers(global.theater_tiers);
  const stored = global.priorities ?? options?.priority_factors.map((f) => f.id) ?? [];
  // Drop factor ids this build has no factor for. `leaving_soon` was removed
  // 2026-08-13 and every stored blob still names it: the solver already scores
  // an unknown factor zero (`planner_rules._priorities`), so the only thing
  // left was this panel rendering the raw snake_case id as a row with working
  // reorder and remove controls. Filtering here also self-heals the blob -- but
  // only on the next PRIORITIES edit, not on the next edit of anything:
  // `setGlobal` patches one key, so editing theater tiers or a day's hours
  // carries the raw stored array through untouched. Harmless while the dropped
  // factor is already inert everywhere it is read, which is the only reason
  // this is an acceptable thing to do outside a versioned migration -- see
  // `planner_rules.migrate_priorities` on why the same shape applied to a LIVE
  // factor would silently destroy a real setting.
  //
  // Guarded on `options` having loaded: an absent vocabulary means "not known
  // yet", not "no factors exist", and filtering against it would blank the
  // list and then save the blank.
  const priorities = options
    ? stored.filter((id) => options.priority_factors.some((f) => f.id === id))
    : stored;

  return (
    <div className="space-y-3">
      <details className={CARD}>
        <summary className="cursor-pointer text-sm font-medium text-neutral-300">
          Global rules
        </summary>

        <details className="rounded border border-neutral-800 p-2">
          <summary className="cursor-pointer text-xs text-neutral-500">
            Theater preference
          </summary>
          <div className="mt-2 space-y-1">
          <span className="block text-xs text-neutral-600">
            A film is only offered at the best-ranked theater it plays at.
          </span>
          <Ordered
            label="Theater preference"
            entries={theaterEntries.map((entry) => ({ key: entry.id }))}
            onMove={(from, to) =>
              onChange(setGlobal(preferences, { theater_tiers: toTiers(move(theaterEntries, from, to)) }))
            }
            render={(index) => {
              const entry = theaterEntries[index];
              const label = options?.theaters.find((t) => t.id === entry.id)?.label ?? entry.id;
              return (
                <>
                  <span className="flex-1 text-neutral-300">{label}</span>
                  {index > 0 && (
                    <label className="flex items-center gap-1 text-xs text-neutral-500">
                      <input
                        type="checkbox"
                        checked={entry.tied}
                        onChange={() =>
                          onChange(setGlobal(preferences, {
                            theater_tiers: toTiers(
                              theaterEntries.map((e, i) => (i === index ? { ...e, tied: !e.tied } : e)),
                            ),
                          }))
                        }
                        className="accent-emerald-400"
                      />
                      Equal to above
                    </label>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${label}`}
                    onClick={() =>
                      onChange(setGlobal(preferences, {
                        theater_tiers: toTiers(theaterEntries.filter((_e, i) => i !== index)),
                      }))
                    }
                    className={`${CHIP} border-neutral-800 text-neutral-500 hover:border-red-800`}
                  >
                    ×
                  </button>
                </>
              );
            }}
          />
          <div className="flex flex-wrap gap-1 pt-1">
            {(options?.theaters ?? [])
              .filter((theater) => !theaterEntries.some((entry) => entry.id === theater.id))
              .map((theater) => (
                <button
                  key={theater.id}
                  type="button"
                  onClick={() =>
                    onChange(setGlobal(preferences, {
                      theater_tiers: toTiers([...theaterEntries, { id: theater.id, tied: false }]),
                    }))
                  }
                  className={`${CHIP} border-neutral-800 text-neutral-500 hover:border-neutral-600`}
                >
                  + {theater.label}
                </button>
              ))}
          </div>
          <p className="text-xs text-neutral-600">
            Anything you do not rank stays below these, in the city's own order.
          </p>
          </div>
        </details>

        <div className="space-y-1">
          <span className="block text-xs text-neutral-500">Days I would rather go</span>
          <div className="flex flex-wrap gap-1">
            {WEEKDAYS.map((label, weekday) => {
              const preferred = (global.preferred_days ?? []).includes(weekday);
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={preferred}
                  onClick={() =>
                    onChange(setGlobal(preferences, {
                      preferred_days: toggle(global.preferred_days, weekday),
                    }))
                  }
                  className={`${CHIP} ${preferred ? "border-emerald-700 text-emerald-300" : "border-neutral-800 text-neutral-500"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-neutral-600">
            A nudge, not a rule. To rule a day out entirely, turn it off below.
          </p>
        </div>

        <DoubleFeatures
          label="Global double features"
          value={global.double_features ?? "preferred"}
          onChange={(next) => onChange(setGlobal(preferences, { double_features: next ?? "preferred" }))}
        />

        <GapField
          label="Global double feature gap"
          value={global.double_feature_gap}
          onChange={(next) =>
            onChange(setGlobal(preferences, { double_feature_gap: next ?? DEFAULT_GAP }))
          }
        />

        <label className="flex items-center gap-2 text-sm text-neutral-400">
          <span className="w-32 shrink-0">Films per day</span>
          <input
            type="number"
            min={1}
            max={4}
            aria-label="Max films per day"
            value={global.max_films ?? 2}
            onChange={(e) =>
              onChange(setGlobal(preferences, { max_films: Number(e.target.value) || 2 }))
            }
            className={`${FIELD} w-20`}
          />
          <span className="text-xs text-neutral-600">
            at most, on any one day (2 is a double feature)
          </span>
        </label>

        <label className="flex items-center gap-2 text-sm text-neutral-400">
          <input
            type="checkbox"
            checked={global.include_discovery_matches !== false}
            onChange={(e) =>
              onChange(setGlobal(preferences, { include_discovery_matches: e.target.checked }))
            }
            className="accent-emerald-400"
          />
          Also suggest films matched to my taste, not only my watchlist
        </label>

        <details className="rounded border border-neutral-800 p-2">
          <summary className="cursor-pointer text-xs text-neutral-500">
            What matters most, in order
          </summary>
          <div className="mt-2 space-y-1">
          <Ordered
            label="Priorities"
            entries={priorities.map((id) => ({ key: id }))}
            onMove={(from, to) =>
              onChange(setGlobal(preferences, { priorities: move(priorities, from, to) }))
            }
            render={(index) => {
              const id = priorities[index];
              const label = options?.priority_factors.find((f) => f.id === id)?.label ?? id;
              return (
                <>
                  <span className="flex-1 text-neutral-300">{label}</span>
                  <button
                    type="button"
                    aria-label={`Ignore ${label}`}
                    onClick={() =>
                      onChange(setGlobal(preferences, {
                        priorities: priorities.filter((_p, i) => i !== index),
                      }))
                    }
                    className={`${CHIP} border-neutral-800 text-neutral-500 hover:border-red-800`}
                  >
                    ×
                  </button>
                </>
              );
            }}
          />
          <div className="flex flex-wrap gap-1 pt-1">
            {(options?.priority_factors ?? [])
              .filter((factor) => !priorities.includes(factor.id))
              .map((factor) => (
                <button
                  key={factor.id}
                  type="button"
                  onClick={() =>
                    onChange(setGlobal(preferences, { priorities: [...priorities, factor.id] }))
                  }
                  className={`${CHIP} border-neutral-800 text-neutral-500 hover:border-neutral-600`}
                >
                  + {factor.label}
                </button>
              ))}
          </div>
          <p className="text-xs text-neutral-600">
            This orders films that are already eligible; the checkbox above decides which are
            eligible at all. Anything removed here is ignored entirely. Measured against real
            hub output, taste separates a watchlist film from the rest but barely distinguishes
            two discovery matches, so the factors below it do most of the ordering.
            </p>
          </div>
        </details>
      </details>

      <div className={CARD}>
        <p className="text-sm font-medium text-neutral-300">Each day</p>
        {WEEKDAYS.map((label, weekday) => {
          const day = preferences.days[String(weekday)] ?? {};
          const open = openDay === weekday;
          return (
            <div key={label} className="rounded border border-neutral-800">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpenDay(open ? null : weekday)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm"
              >
                <span className="w-10 font-medium text-neutral-300">{label}</span>
                <span className="flex-1 text-xs text-neutral-500">
                  {describeDay(preferences, weekday)}
                </span>
                <span className="text-xs text-neutral-600">{open ? "−" : "+"}</span>
              </button>
              {open && (
                <div className="space-y-3 border-t border-neutral-800 px-3 py-3">
                  <label className="flex items-center gap-2 text-sm text-neutral-400">
                    <input
                      type="checkbox"
                      checked={day.enabled !== false}
                      onChange={(e) =>
                        onChange(setDay(preferences, weekday, { enabled: e.target.checked }))
                      }
                      className="accent-emerald-400"
                    />
                    Available on {label}
                  </label>
                  <span className="block text-xs text-neutral-500">
                    Start times I am free on {label}
                  </span>
                  {/* No `inherited` prop: hours are per-day only now, with no
                      global to inherit from. Passing one claimed a restriction
                      the solver does not apply -- an old stored blob still
                      carries `global.windows`, and nothing strips it. */}
                  <Windows
                    idPrefix={label}
                    value={day.windows}
                    onChange={(next) => onChange(setDay(preferences, weekday, { windows: next }))}
                  />
                  <span className="block text-xs text-neutral-500">
                    Theaters I will go to on {label}
                  </span>
                  {/* An allowlist, not a ranking. Checking nothing means "any",
                      which is why the help text says so rather than leaving an
                      empty row to be read as "nowhere". */}
                  <div className="flex flex-wrap gap-1">
                    {(options?.theaters ?? []).map((theater) => {
                      const picked = (day.theaters ?? []).includes(theater.id);
                      return (
                        <button
                          key={theater.id}
                          type="button"
                          aria-pressed={picked}
                          onClick={() => {
                            const next = toggle(day.theaters ?? [], theater.id);
                            onChange(setDay(preferences, weekday, {
                              theaters: next.length > 0 ? next : undefined,
                            }));
                          }}
                          className={`${CHIP} ${picked
                            ? "border-emerald-800 text-emerald-300"
                            : "border-neutral-800 text-neutral-500 hover:border-neutral-600"}`}
                        >
                          {theater.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-neutral-600">
                    {(day.theaters ?? []).length === 0
                      ? `Nothing selected, so any theater is fine on ${label}.`
                      : `On ${label} only these are offered. Films playing nowhere on this list are suggested on another day instead, at whichever theater your ranking prefers.`}
                  </p>
                  <label className="flex items-center gap-2 text-sm text-neutral-400">
                    <span className="w-32 shrink-0">Ideal start time</span>
                    <input
                      type="time"
                      aria-label={`${label} preferred start`}
                      value={day.preferred_start != null ? toClock(day.preferred_start) : ""}
                      onChange={(e) =>
                        onChange(setDay(preferences, weekday, {
                          preferred_start: toMinutes(e.target.value) ?? undefined,
                        }))
                      }
                      className={FIELD}
                    />
                    {day.preferred_start == null && (
                      <span className="text-xs text-neutral-600">no target</span>
                    )}
                  </label>
                  <DoubleFeatures
                    label={`${label} double features`}
                    value={day.double_features}
                    inherited={global.double_features ?? "preferred"}
                    onChange={(next) =>
                      onChange(setDay(preferences, weekday, { double_features: next }))
                    }
                  />
                  <GapField
                    label={`${label} double feature gap`}
                    value={day.double_feature_gap}
                    inherited={global.double_feature_gap ?? DEFAULT_GAP}
                    onChange={(next) =>
                      onChange(setDay(preferences, weekday, { double_feature_gap: next }))
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
